import type { Dayjs } from "dayjs";

import GoApi from "@/libs/client/goApi/goApi";
import type {
  DataInterface,
  ResponseHolidayDataInterface,
} from "@/libs/client/goApi/holidays/holidayServiceInterface";
import dayjs from "@/utils/dayjs";

import { toDayjs } from "./dateConvertFormatUtills";

const callHolidayData = async () => {
  try {
    const holidayDataResponse =
      (await GoApi.holiday.getHolidayData()) as ResponseHolidayDataInterface;
    if (!holidayDataResponse || !holidayDataResponse.success) return undefined;
    return holidayDataResponse.data;
  } catch (error) {
    return undefined;
  }
};

// Get the week of the year
const getWeekNumber = (day: Date | Dayjs) => {
  const d = toDayjs(day);
  const startOfYear = dayjs(Date.UTC(d.year(), 0, 1));
  const dayOfYear = d.diff(startOfYear, "day") + 1;

  // Calculate week number based on the first Thursday of the year
  const adjustedDay =
    dayOfYear +
    (startOfYear.day() <= 4 ? startOfYear.day() : startOfYear.day() - 7);
  return Math.ceil(adjustedDay / 7);
};

// Get near holiday
export const getNearPublicHolidayInWeekend = async (
  currentDate: Date | Dayjs,
): Promise<
  { nearHoliday: DataInterface; allHolidayOnWeak?: DataInterface[] } | undefined
> => {
  const holidays = await callHolidayData();
  if (!holidays) return undefined;
  const currentMonth = toDayjs(currentDate);

  // if current date is Saturday or Sunday
  if (currentMonth.day() === 6) return undefined;
  if (currentMonth.day() === 0) {
    return {
      nearHoliday: {
        date: {
          start: {
            timestamp: currentMonth.valueOf(), // current is Sunday,
          },
          end: {
            timestamp: currentMonth.clone().add(1, "day").valueOf(), // next day is Monday,
          },
        },
      },
    };
  }

  // find all holidays in the current week
  const findNearHoliday = holidays.filter((holiday) => {
    const holidayDateEndFormat = toDayjs(holiday.date.end.timestamp);
    // in same year
    if (currentMonth.year() !== holidayDateEndFormat.year()) return false;
    // in same month
    if (currentMonth.month() !== holidayDateEndFormat.month()) return false;
    if (getWeekNumber(currentMonth) !== getWeekNumber(holidayDateEndFormat))
      return false;
    return true;
  });

  // holiday after the current date
  const afterCurrentDate = findNearHoliday.filter(
    (after) => after.date.start.timestamp >= currentMonth.valueOf(),
  );

  return {
    nearHoliday: afterCurrentDate[0] /* get first */ as DataInterface,
    allHolidayOnWeak: findNearHoliday,
  };
};

export function findNearestWeekend(date?: Date | Dayjs): {
  nearestSaturday: Dayjs;
  nearestSunday: Dayjs;
} {
  const currentDate = date ? toDayjs(date) : dayjs();

  const currentDay = currentDate.day();

  // Calculate the nearest Saturday and set it to the start of the day
  const nearestSaturday = currentDate
    .clone()
    .add(6 - currentDay, "day")
    .startOf("day");

  // Clone nearestSaturday to avoid mutation and then calculate nearest Sunday
  const nearestSunday = nearestSaturday.clone().add(1, "day").startOf("day");

  return {
    nearestSaturday,
    nearestSunday,
  };
}

export function getNextFridayAndDaysAfter(
  currentDate?: Date | Dayjs,
  nextDayNumber: number = 3,
): { friday: Dayjs; nextTDays: Dayjs } {
  const today = toDayjs(currentDate || dayjs());
  let friday;

  if (today.day() === 5) {
    friday = today.add(7, "day");
  } else {
    const daysUntilNextFriday = (5 - today.day() + 7) % 7;
    friday = today.add(daysUntilNextFriday, "day");
  }

  const nextTDays = friday.clone().add(nextDayNumber, "day");

  return {
    friday,
    nextTDays,
  };
}

export function getFirstDayOfPreviousMonth(inputDate: Date | Dayjs): Dayjs {
  const date = toDayjs(inputDate);
  return date.subtract(1, "month").startOf("month");
}

export function getPastByDays(
  dateDefault: Date | Dayjs,
  daysOfPast: number = 60,
): Dayjs[] {
  const today = toDayjs(dateDefault).startOf("day");
  const pastDays: any[] = [];

  for (let i = 0; i < daysOfPast; i += 1) {
    pastDays.push(today.subtract(i, "day"));
  }

  return pastDays;
}

export const getNextDay = (
  currentDate: Date | Dayjs = dayjs(),
  day: number = 1,
) => {
  return toDayjs(currentDate).add(day, "day");
};

export function setMidnight(date: Date | Dayjs): Dayjs {
  return toDayjs(date).startOf("day");
}

export const compareDate = (
  dateStart?: Date | Dayjs,
  dateEnd?: Date | Dayjs,
) => {
  try {
    if (!dateStart || !dateEnd) return false;
    return toDayjs(dateStart).isSame(toDayjs(dateEnd), "day");
  } catch (error) {
    return false;
  }
};

export const isDateInRange = (
  targetDate: Date | Dayjs,
  startDate: Date | Dayjs,
  endDate: Date | Dayjs,
) => {
  try {
    return toDayjs(targetDate).isBetween(
      toDayjs(startDate),
      toDayjs(endDate),
      null,
      "[]",
    ); // Inclusive range
  } catch (error) {
    return false;
  }
};

export const nextOrBackYear = (
  currentDate_: Date | Dayjs,
  type: "next" | "back" = "next",
  nextYear: number = 1,
): Dayjs => {
  const currentDate = toDayjs(currentDate_); // Convert to Day.js object
  const newYear =
    type === "next"
      ? currentDate.year() + nextYear
      : currentDate.year() - nextYear;

  // Create a new Day.js object with the updated year, preserving month and date
  const dateNextYear = currentDate.year(newYear);

  return dateNextYear; // Return the new Day.js object
};

export const updateYear = (
  yearOffset: number = 0,
  date: Dayjs = dayjs(),
): Dayjs => {
  return (date || dayjs()).add(yearOffset, "year");
};

export function getNextTwoWeeksMondayAndDaysAfter(
  currentDate: Dayjs = dayjs(),
  nextDayNumber: number = 1,
): { nextTwoWeeksMonday: Dayjs; nextTDays: Dayjs } {
  // Get today's date or use the provided current date
  const today = currentDate;

  // Calculate days until the next Monday
  const currentDay = today.day();
  const daysUntilNextTwoWeeksMonday = currentDay === 0 ? 8 : 15 - currentDay;

  // Get nextTwoWeeksMonday as a Dayjs object
  const nextTwoWeeksMonday = today.add(daysUntilNextTwoWeeksMonday, "day");

  // Set nextTDays to the specified number of days after nextTwoWeeksMonday
  const nextTDays = nextTwoWeeksMonday.clone().add(nextDayNumber, "day");

  // Return an object containing nextTwoWeeksMonday and nextTDays
  return {
    nextTwoWeeksMonday,
    nextTDays,
  };
}
